import { QueryClient, QueryFunction } from "@tanstack/react-query";
import { reportApiError } from "./errorReporter";
import { clearSessionIndicator } from '@/hooks/useAuth';

// Auth error reason codes from backend
export type AuthErrorReason = 'NO_SESSION' | 'SESSION_EXPIRED' | 'USER_NOT_FOUND' | 'USER_INACTIVE' | 'SESSION_ERROR' | 'ROLE_REQUIRED' | 'INVALID_CREDENTIALS';

// Custom error class for auth redirects - thrown when redirecting, not a real error
// This prevents unhandled rejection errors while still stopping the request chain
export class AuthRedirectError extends Error {
  constructor(message: string = 'Redirecting due to authentication issue') {
    super(message);
    this.name = 'AuthRedirectError';
  }
}

// List of endpoints where 401 is expected (session checks, auth checks, login attempts)
// These are exact path matches (extracted from URL)
const EXPECTED_401_ENDPOINTS = [
  '/api/auth/user',
  '/api/session/check',
  '/api/login',
  '/api/auth/login',
  '/api/admin/badge-counts', // May be called before auth check completes during navigation
];

// Endpoints where 403 is expected (authorization checks, accessing protected resources)
// These use prefix matching for patterns like /api/orders/:id, /api/users/addresses/:id
const EXPECTED_403_PATTERNS = [
  '/api/orders/',          // Order access checks
  '/api/my/orders/',       // Customer order access
  '/api/users/addresses/', // Address ownership checks
  '/api/invoices/',        // Invoice access
  '/api/admin/',           // Admin-only routes
  '/api/claims/',          // Claims access
];

// Check if a 401 is expected for this endpoint (shouldn't trigger error logging or redirect)
function isExpected401(url: string): boolean {
  try {
    // Extract path from URL (handles both full URLs and relative paths)
    const path = url.startsWith('http') ? new URL(url).pathname : url.split('?')[0];
    // Exact match only - prevents /api/user from matching /api/users
    return EXPECTED_401_ENDPOINTS.includes(path);
  } catch {
    return false;
  }
}

// Check if a 403 is expected for this endpoint (resource access/authorization checks)
function isExpected403(url: string): boolean {
  try {
    const path = url.startsWith('http') ? new URL(url).pathname : url.split('?')[0];
    return EXPECTED_403_PATTERNS.some(pattern => path.startsWith(pattern));
  } catch {
    return false;
  }
}

// Handle 403 Forbidden - redirect to home with friendly message
// Returns true if redirect was initiated, false otherwise
function handle403Forbidden(url: string): boolean {
  const currentPath = window.location.pathname;
  
  // Don't redirect if already on public pages
  if (isPublicPath(currentPath)) {
    return false;
  }
  
  // Store current location for redirect after login (consistent with 401 handling)
  const returnUrl = window.location.pathname + window.location.search;
  if (returnUrl && returnUrl !== '/login' && returnUrl !== '/') {
    sessionStorage.setItem('auth_redirect', returnUrl);
  }
  
  // Store a message to show after redirect
  sessionStorage.setItem('auth_message', 'You don\'t have permission to access that page.');
  
  // Redirect to home
  window.location.href = '/';
  return true;
}

// Public paths that don't require authentication - centralized list
export const PUBLIC_PATHS = [
  '/',           // Showroom home
  '/login', 
  '/signup', 
  '/splash', 
  '/forgot-password', 
  '/set-password', 
  '/setup-account', 
  '/product',    // Product detail pages
  '/products', 
  '/grading',
  '/api-docs',
  '/api-access',
  '/schedule-pickup',
  '/coming-soon' // Coming Soon landing pages
];

// Check if a path is public (doesn't require auth)
export function isPublicPath(path: string): boolean {
  // Special case for root path - exact match
  if (path === '/') return true;
  
  // For other paths, check if it starts with a public path prefix
  // Skip root '/' in prefix matching since all paths start with /
  return PUBLIC_PATHS.filter(p => p !== '/').some(p => path.startsWith(p));
}

// Redirect to showroom when session expires (only for protected pages)
function handleSessionExpired() {
  // Clear the session indicator first - this prevents repeated 401 requests
  // This synchronously updates the global state and notifies all useAuth subscribers
  clearSessionIndicator();
  
  const currentPath = window.location.pathname;
  
  // Don't redirect if already on public pages
  if (isPublicPath(currentPath)) {
    return;
  }
  
  // Store current location for redirect after login
  const returnUrl = window.location.pathname + window.location.search;
  if (returnUrl && returnUrl !== '/login' && returnUrl !== '/') {
    sessionStorage.setItem('auth_redirect', returnUrl);
  }
  
  // Redirect to showroom - let them browse as guest
  window.location.href = '/';
}

async function throwIfResNotOk(res: Response, url?: string, method?: string) {
  if (!res.ok) {
    const contentType = res.headers.get("content-type");
    let errorMessage: string;
    let authReason: AuthErrorReason | undefined;
    
    if (contentType?.includes("application/json")) {
      try {
        const json = await res.json();
        // Extract auth reason if present
        authReason = json.reason as AuthErrorReason | undefined;
        // If there's a message property, use it as the error message
        errorMessage = json.message || json.error || `${res.status}: ${JSON.stringify(json)}`;
      } catch (e) {
        // If parsing fails, fall back to text
        errorMessage = `${res.status}: ${res.statusText}`;
      }
    } else {
      const text = (await res.text()) || res.statusText;
      errorMessage = `${res.status}: ${text}`;
    }
    
    const error = new Error(errorMessage);
    
    // Handle 401 - session expired
    if (res.status === 401) {
      if (!isExpected401(url || '')) {
        // Only log unexpected 401s - expected ones are normal session checks
        // Include the reason code for better debugging
        const logMessage = authReason 
          ? `[Auth] Session issue (${authReason}): ${errorMessage}`
          : `[Auth] Unauthorized: ${errorMessage}`;
        console.log(logMessage);
        
        // Only report to error logging if it's not an expected auth scenario
        // NO_SESSION, SESSION_EXPIRED, and INVALID_CREDENTIALS are expected, not errors
        // If no reason is provided, still log the error for monitoring (legacy endpoints)
        const expectedReasons = ['NO_SESSION', 'SESSION_EXPIRED', 'INVALID_CREDENTIALS', 'EMAIL_NOT_FOUND'];
        if (!authReason || !expectedReasons.includes(authReason)) {
          reportApiError(error, url || '', method || 'GET', res.status);
        }
      }
      // Always redirect to login for 401 (unless on expected endpoints)
      if (!isExpected401(url || '')) {
        handleSessionExpired();
      }
    } else if (res.status === 403) {
      // Handle 403 - Forbidden (authorization failure)
      if (isExpected403(url || '')) {
        const path = (url || '').startsWith('http') ? new URL(url || '').pathname : (url || '').split('?')[0];
        const currentPath = window.location.pathname;
        const isAdminRequest = path.startsWith('/api/admin/');
        const isOnAdminPage = currentPath.startsWith('/admin');

        if (isAdminRequest && isOnAdminPage) {
          console.warn(`[Auth] 403 on admin API (suppressed): ${url}`);
          throw new AuthRedirectError('Admin API access denied - suppressed');
        }

        console.log(`[Auth] Access denied for: ${url}`);
        const didRedirect = handle403Forbidden(url || '');
        throw new AuthRedirectError(
          didRedirect 
            ? 'Redirecting to home due to access denied' 
            : 'Access denied on public page'
        );
      } else {
        console.warn(`[Auth] 403 access denied: ${url}`);
      }
    } else if (res.status === 429) {
      // 429 Too Many Requests - transient rate limit, not a bug worth alerting on
      console.warn(`[RateLimit] 429 on ${url} - back off and retry`);
    } else if (res.status === 409) {
      // 409 Conflict - expected business state (e.g. "scan already in progress",
      // "import already in progress"). The UI handles these with a friendly toast;
      // they should never pollute the operations error inbox.
      console.warn(`[API] 409 conflict on ${url} - ${errorMessage}`);
    } else if (res.status === 404) {
      // 404 Not Found is expected for content-not-found scenarios (deleted
      // products, bots hitting old URLs, mistyped slugs). The UI handles
      // these gracefully (e.g. product-detail.tsx shows "Model not found").
      // Don't pollute the error inbox with them — log locally and let the
      // error propagate to the caller for normal handling.
      console.warn(`[API] 404 on ${url} - resource not found`);
    } else if (url && !isExpected401(url)) {
      // Report other API errors (but not expected 401s)
      reportApiError(error, url, method || 'GET', res.status);
    }
    
    throw error;
  }
}

export async function apiRequest(
  method: string,
  url: string,
  data?: unknown | undefined,
): Promise<any> {
  const isFormData = data instanceof FormData;
  
  const res = await fetch(url, {
    method,
    headers: isFormData ? {} : data ? { "Content-Type": "application/json" } : {},
    body: isFormData ? data : data ? JSON.stringify(data) : undefined,
    credentials: "include",
  });

  try {
    await throwIfResNotOk(res, url, method);
  } catch (err) {
    // Swallow AuthRedirectError - redirect is already initiated, return null to complete gracefully
    if (err instanceof AuthRedirectError) {
      return null;
    }
    throw err;
  }
  
  // Handle empty responses (204 No Content, etc.)
  const contentType = res.headers.get("content-type");
  if (res.status === 204 || !contentType?.includes("application/json")) {
    return null;
  }
  
  return await res.json();
}

type UnauthorizedBehavior = "returnNull" | "throw";
export const getQueryFn: <T>(options: {
  on401: UnauthorizedBehavior;
}) => QueryFunction<T> =
  ({ on401: unauthorizedBehavior }) =>
  async ({ queryKey }) => {
    const url = queryKey.join("/") as string;
    const res = await fetch(url, {
      credentials: "include",
    });

    if (unauthorizedBehavior === "returnNull" && res.status === 401) {
      return null;
    }

    try {
      await throwIfResNotOk(res, url, 'GET');
    } catch (err) {
      // Swallow AuthRedirectError - redirect is already initiated, return null to complete gracefully
      if (err instanceof AuthRedirectError) {
        return null;
      }
      throw err;
    }
    return await res.json();
  };

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      queryFn: getQueryFn({ on401: "throw" }),
      refetchInterval: false,
      refetchOnWindowFocus: false,
      staleTime: Infinity,
      retry: false,
      gcTime: 0, // Disable garbage collection time - queries don't persist across page reloads
    },
    mutations: {
      retry: false,
    },
  },
});
